import { AfterViewInit, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

import { Customer } from '@auth/models/customer';
import { CustomerRegistationFormData } from '@auth/models/customer-registration-data';
import { RpcInputOptions } from '@core-controls/components/rpc-input/models/rpc-input-options';
import { ValidationService } from '@core-validation/validation.service';
import { RpcRoute } from '@core-layout/app/models/rpc-route';
import { PRIVACY_POLICY_LINK, TERMS_AND_CONDITIONS_LINK } from '@auth/constants/terms-and-conditions';
import { RpcCheckboxState } from '@core-controls/components/rpc-checkbox/types/rpc-checkbox-state';
import { PhoneService } from '@core-services/phone/phone.service';
import { CountryPhoneInfo } from '@core-services/phone/models/country-phone-info';
import { RpcInputComponent } from '../../../../core-modules/controls/components/rpc-input/rpc-input.component';
import { PhoneFormInputComponent } from '../../../../core-modules/controls/components/phone-form-input/phone-form-input.component';
import { PasswordComponent } from '../../../../core-modules/controls/components/password/password.component';
import { RpcCheckboxComponent } from '../../../../core-modules/controls/components/rpc-checkbox/rpc-checkbox.component';
import { RpcButtonComponent } from '../../../../core-modules/controls/components/rpc-button/rpc-button.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'registration-form',
    templateUrl: './registration-form.component.html',
    styleUrls: ['./registration-form.component.scss'],
    imports: [FormsModule, ReactiveFormsModule, RpcInputComponent, PhoneFormInputComponent, PasswordComponent, RpcCheckboxComponent, RpcButtonComponent, TranslateModule]
})
export class RegistrationFormComponent implements OnInit, AfterViewInit, OnDestroy {

    @Input() public customerData: Customer;
    @Input() public isWorkspaceAddition: boolean;
    @Output() public formSubmited = new EventEmitter<CustomerRegistationFormData>();

    public readonly rpcRoute = RpcRoute;

    public registrationForm: UntypedFormGroup;
    public firstNameControlOptions: RpcInputOptions;
    public lastNameControlOptions: RpcInputOptions;
    public emailControlOptions: RpcInputOptions;
    public passwordControlOptions: RpcInputOptions;
    public passwordConfirmControlOptions: RpcInputOptions;
    public agreeToTermsAndConditions: RpcCheckboxState = 'unchecked';

    public termsAndConditionsLink = TERMS_AND_CONDITIONS_LINK;
    public privacyPolicyLink = PRIVACY_POLICY_LINK;
    public readonly countriesPhoneInfo = inject(PhoneService).countryPhoneInfo;

    private readonly subscription = new Subscription();

    constructor(
        private readonly formBuilder: UntypedFormBuilder,
        private readonly validationService: ValidationService
    ) { }

    public ngOnInit(): void {
        this.buildForm();
    }

    public ngAfterViewInit(): void {
        setTimeout(() => {
            if (!this.isWorkspaceAddition) {
                this.addSubscriptions();
            }

            this.registrationForm.patchValue(this.customerData);
        });
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public onSubmit(): void {
        const customer = new CustomerRegistationFormData();
        const phwneForm = this.registrationForm.controls.phoneNumber.value as { id: number, phoneNumber: string; countryCode: CountryPhoneInfo };

        customer.id = this.customerData.id;
        customer.firstName = this.registrationForm.controls.firstName.value as string;
        customer.lastName = this.registrationForm.controls.lastName.value as string;
        customer.email = this.registrationForm.controls.email.value as string;
        customer.phoneNumberInfo = { id: phwneForm.id, phoneNumber: phwneForm.phoneNumber, countryCode: phwneForm.countryCode.codeName, isAlertReceiver: false };

        if (!this.isWorkspaceAddition) {
            customer.password = this.registrationForm.controls.password.value as string;
            customer.confirmPassword = this.registrationForm.controls.passwordConfirm.value as string;
        }

        this.formSubmited.emit(customer);
    }

    public onAgreeToTermsAndConditionsChange(state: RpcCheckboxState): void {
        this.agreeToTermsAndConditions = state;
    }

    private buildForm(): void {
        this.registrationForm = this.formBuilder.group({});

        this.firstNameControlOptions = {
            formGroup: this.registrationForm,
            controlName: 'firstName',
            validatorsMap: [
                {
                    message: 'REGISTRATION_FORM.VALIDATION_ERRORS.FIRST_NAME_REQUIRED',
                    showError: (control: UntypedFormControl) => control.hasError('required'),
                    validator: Validators.required
                }
            ]
        };

        this.lastNameControlOptions = {
            formGroup: this.registrationForm,
            controlName: 'lastName',
            validatorsMap: [
                {
                    message: 'REGISTRATION_FORM.VALIDATION_ERRORS.LAST_NAME_REQUIRED',
                    showError: (control: UntypedFormControl) => control.hasError('required'),
                    validator: Validators.required
                }
            ]
        };

        this.lastNameControlOptions = {
            formGroup: this.registrationForm,
            controlName: 'lastName',
            validatorsMap: [
                {
                    message: 'REGISTRATION_FORM.VALIDATION_ERRORS.LAST_NAME_REQUIRED',
                    showError: (control: UntypedFormControl) => control.hasError('required'),
                    validator: Validators.required
                }
            ]
        };

        this.emailControlOptions = {
            formGroup: this.registrationForm,
            controlName: 'email',
            validatorsMap: [
                {
                    message: 'REGISTRATION_FORM.VALIDATION_ERRORS.EMAIL_REQUIRED',
                    showError: (control: UntypedFormControl) => control.hasError('required'),
                    validator: Validators.required
                },
                {
                    message: 'REGISTRATION_FORM.VALIDATION_ERRORS.WRONG_EMAIL',
                    showError: (control: UntypedFormControl) => control.hasError('email'),
                    validator: Validators.email
                }
            ]
        };

        this.passwordControlOptions = {
            formGroup: this.registrationForm,
            controlName: 'password',
            validatorsMap: [
                {
                    message: 'REGISTRATION_FORM.VALIDATION_ERRORS.PASSWORD_REQUIRED',
                    showError: (control: UntypedFormControl) => control.hasError('required'),
                    validator: Validators.required
                },
                {
                    message: 'REGISTRATION_FORM.VALIDATION_ERRORS.NUMBER_REQIRED',
                    showError: (control: UntypedFormControl) => control.hasError('noNumber'),
                    validator: this.validationService.getPasswordValidator()
                },
                {
                    message: 'REGISTRATION_FORM.VALIDATION_ERRORS.CAPITAL_REQUIRED',
                    showError: (control: UntypedFormControl) => control.hasError('noCapitalCase'),
                    validator: this.validationService.getPasswordValidator()
                },
                {
                    message: 'REGISTRATION_FORM.VALIDATION_ERRORS.SMALL_REQUIRED',
                    showError: (control: UntypedFormControl) => control.hasError('noSmallCase'),
                    validator: this.validationService.getPasswordValidator()
                },
                {
                    message: 'REGISTRATION_FORM.VALIDATION_ERRORS.SPECIAL_REQIRED',
                    showError: (control: UntypedFormControl) => control.hasError('noSpecialCharacter'),
                    validator: this.validationService.getPasswordValidator()
                },
                {
                    message: 'REGISTRATION_FORM.VALIDATION_ERRORS.MINIMUM_LENGTH',
                    showError: (control: UntypedFormControl) => control.hasError('minlength'),
                    validator: this.validationService.getPasswordValidator()
                },
                {
                    message: 'REGISTRATION_FORM.VALIDATION_ERRORS.MAXIMUM_LENGTH',
                    showError: (control: UntypedFormControl) => control.hasError('maxlength'),
                    validator: this.validationService.getPasswordValidator()
                }
            ]
        };

        this.passwordConfirmControlOptions = {
            formGroup: this.registrationForm,
            controlName: 'passwordConfirm',
            validatorsMap: [
                {
                    message: 'REGISTRATION_FORM.VALIDATION_ERRORS.PASSWORD_CONFIRMATION_REQUIRED',
                    showError: (control: UntypedFormControl) => control.hasError('required'),
                    validator: Validators.required
                },
                {
                    message: 'REGISTRATION_FORM.VALIDATION_ERRORS.PASSWORDS_NOT_MATCH',
                    showError: (control: UntypedFormControl) => control.hasError('notMatching'),
                    validator: this.validationService.getMatchValidator('password')
                }
            ]
        };
    }

    private addSubscriptions(): void {
        this.subscription.add(this.registrationForm.get('password').valueChanges
            .subscribe(() => {
                this.registrationForm.get('passwordConfirm').updateValueAndValidity();
            }));
    }
}